import React, { useMemo } from "react"

import { Tooltip, type ColumnDef } from "@coval/ui"

import type { RunRowLike } from "@/types/RunRow"

import { createRunResultsCellRenderer } from "../components/run-results-table/RunResultsCell"
import {
  getRunResultsColumnMinWidthPx,
  parsePixelWidth,
  type RunResultsTableColumn,
} from "./useRunResultsTableColumns"

const METRIC_COLUMN_WIDTH_PX = 100
const FIXED_WIDTH_COLUMN_KEYS = new Set(["index", "errorStatus"])

const MetricColumnHeader: React.FC<{ label: string }> = ({ label }) => (
  <Tooltip content={label}>
    <div className="text-xs whitespace-normal wrap-break-word leading-tight line-clamp-3 overflow-hidden text-center min-w-0 max-w-full">
      {label}
    </div>
  </Tooltip>
)

const metricColumnHeaderRenderers = new Map<string, () => React.ReactNode>()

function getMetricColumnHeaderRenderer(label: string): () => React.ReactNode {
  const existingRenderer = metricColumnHeaderRenderers.get(label)
  if (existingRenderer) {
    return existingRenderer
  }

  const renderer = () => <MetricColumnHeader label={label} />
  metricColumnHeaderRenderers.set(label, renderer)
  return renderer
}

type RunResultsCellRendererParams = Parameters<
  typeof createRunResultsCellRenderer
>[0]

interface UseRunResultsRenderColumnsParams extends Omit<
  RunResultsCellRendererParams,
  "columnKey"
> {
  visibleOrderedColumns: RunResultsTableColumn[]
}

export function useRunResultsRenderColumns({
  agentsById,
  compareColumnLabel,
  explanationTable,
  getCompareColor,
  getCompareDisplayValue,
  metricIdFilter,
  metricRenderAdaptersById,
  mutationById,
  mutationNameById,
  onListenLive,
  outlierEvidenceBySimulationId,
  runRecord,
  scopedRowDisplayIndex,
  scopedRunRows,
  visibleOrderedColumns,
}: UseRunResultsRenderColumnsParams): ColumnDef<RunRowLike>[] {
  return useMemo(
    () =>
      visibleOrderedColumns.map((column) => {
        const isMetric = column.key in metricRenderAdaptersById
        const isRightPinned = column.key === "simulationOutputId"
        const isFixedWidth =
          isMetric || isRightPinned || FIXED_WIDTH_COLUMN_KEYS.has(column.key)
        const minWidth = getRunResultsColumnMinWidthPx(
          column,
          metricRenderAdaptersById,
        )
        const label = column.fullLabel || column.label

        return {
          id: column.key,
          header: isMetric ? getMetricColumnHeaderRenderer(label) : label,
          headerLabel: label,
          align: isMetric ? "center" : undefined,
          minWidth,
          width: isMetric
            ? METRIC_COLUMN_WIDTH_PX
            : isFixedWidth
              ? minWidth
              : (parsePixelWidth(column.width) ?? minWidth),
          pinned: isRightPinned ? "right" : undefined,
          flex: !isFixedWidth,
          sortable: false,
          enableReordering: false,
          enableHiding: false,
          cell: createRunResultsCellRenderer({
            agentsById,
            columnKey: column.key,
            compareColumnLabel,
            explanationTable,
            getCompareColor,
            getCompareDisplayValue,
            metricIdFilter,
            metricRenderAdaptersById,
            mutationById,
            mutationNameById,
            onListenLive,
            outlierEvidenceBySimulationId,
            runRecord,
            scopedRowDisplayIndex,
            scopedRunRows,
          }),
        }
      }),
    [
      agentsById,
      compareColumnLabel,
      explanationTable,
      getCompareColor,
      getCompareDisplayValue,
      metricIdFilter,
      metricRenderAdaptersById,
      mutationById,
      mutationNameById,
      onListenLive,
      outlierEvidenceBySimulationId,
      runRecord,
      scopedRowDisplayIndex,
      scopedRunRows,
      visibleOrderedColumns,
    ],
  )
}
